import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select, Drawer } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getProductDecisions } from '../../services/rapportService';
import {
  NdoePage, NdoePageHeader, NdoePeriodPicker, NdoeActionButton,
  NdoeResponsiveTable, NdoeDataCard, NdoeStatusBadge, NdoeImage,
  NdoeStats, NdoeStatCard, NdoeError,
} from '../../components/ndoe';
import { toCsv, downloadCsv } from './exportCsv';
import useRefLists from './useRefLists';
import ReportFilters from './ReportFilters';
import { dateTxt, CONFIANCE_COURT } from './format';
import './rapports.css';

const typeBadge = (row) => (
  <NdoeStatusBadge kind="custom" tone={row.type_tone} label={row.type_label} />
);

// Chaque action renvoie vers le rapport qui l'a produite : on peut toujours vérifier.
const SOURCE_ROUTE = {
  ruptures: '/rapports/ruptures',
  rotation: '/rapports/rotation',
  dormants: '/rapports/dormants',
  pointures: '/rapports/pointures',
};

const SORTS = [
  { value: 'priorite', label: 'Priorité' },
];
const TYPES = [
  { value: '', label: 'Toutes les décisions' },
  { value: 'reassort', label: 'Réassortir' },
  { value: 'reassort_preventif', label: 'Réassortir avant rupture' },
  { value: 'renforcer_pointure', label: 'Renforcer une pointure' },
  { value: 'remettre_en_avant', label: 'Remettre en avant' },
  { value: 'tester', label: 'À tester' },
  { value: 'solder', label: 'Solder' },
  { value: 'reduire_pointure', label: 'Réduire une pointure' },
  { value: 'ne_pas_recommander', label: 'Ne pas recommander' },
  { value: 'surveiller', label: 'Surveiller' },
];

const EMPTY_FILTERS = { marque: '', categorie: '', matiere: '', couleur: '', pointure: '', type: '' };

export default function DecisionsRecommandees() {
  const user = useSelector((s) => s.user?.currentUser);
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';
  const navigate = useNavigate();

  const [period, setPeriod] = useState({ period: 30 });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [prioritairesSeules, setPrioritairesSeules] = useState(true);

  const [payload, setPayload] = useState({ meta: {}, summary: {}, data: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [detail, setDetail] = useState(null);

  const refs = useRefLists(isAdmin);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(q.trim()), 400);
    return () => clearTimeout(timer);
  }, [q]);

  const apiParams = useMemo(() => ({
    period: period.period === 'custom' ? undefined : period.period,
    start_date: period.period === 'custom' ? period.start_date : undefined,
    end_date: period.period === 'custom' ? period.end_date : undefined,
    ...filters, q: search,
  }), [period, filters, search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getProductDecisions(apiParams);
      setPayload(data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Impossible de charger les décisions recommandées.');
    } finally { setLoading(false); }
  }, [apiParams]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const setFilter = (key) => (value) => setFilters((f) => ({ ...f, [key]: value ?? '' }));
  const activeCount = Object.values(filters).filter(Boolean).length + (search ? 1 : 0);

  const { summary = {}, meta = {}, data = [] } = payload;
  const seuil = summary.seuil_priorite ?? 60;

  // La longue traîne (produits jamais vendus à tester, dormants à solder) est réelle
  // mais pas urgente : par défaut on n'affiche que les actions prioritaires.
  const visibles = prioritairesSeules ? data.filter((a) => a.actionnable && a.priorite >= seuil) : data;

  const handleExport = async () => {
    setExporting(true);
    try {
      const columns = [
        { title: 'Priorité', value: (r) => r.priorite },
        { title: 'Décision', value: (r) => r.type_label },
        { title: 'Cible', value: (r) => (r.cible === 'pointure' ? 'Pointure' : 'Produit') },
        { title: 'Nom', value: (r) => r.nom },
        { title: 'Marque', value: (r) => r.marque || '' },
        { title: 'Catégorie', value: (r) => r.categorie || '' },
        { title: 'Raison', value: (r) => r.raison },
        { title: 'Preuves', value: (r) => r.preuve.map((p) => `${p.label} : ${p.valeur}`).join(' | ') },
        { title: 'Confiance donnée', value: (r) => r.confiance_label || '' },
        { title: 'Source', value: (r) => r.source },
      ];
      const { start_date: from, end_date: to } = meta;
      downloadCsv(`decisions_${from || ''}_${to || ''}.csv`, toCsv(columns, visibles));
      toast.success(`${visibles.length} décision(s) exportée(s).`);
    } catch (e) {
      toast.error("L'export a échoué.");
    } finally { setExporting(false); }
  };

  if (!isAdmin) {
    return (
      <NdoePage>
        <NdoeError title="Accès réservé" text="Le module Rapports est réservé à l'administrateur." />
      </NdoePage>
    );
  }

  // Largeurs recalibrées (mise en page 20/07) : « Cible » portait width:60 pour
  // une image de 64 px → l'en-tête se cassait en « CIBL/E » ; « Décision » et
  // « Priorité » gaspillaient l'espace que « Pourquoi » (le contenu utile)
  // n'avait plus. Total fixe 416 px + Quoi/Pourquoi fluides.
  const columns = [
    {
      title: 'Priorité', dataIndex: 'priorite', width: 84, align: 'center',
      render: (v) => (
        <div className={`dc-prio ${v >= 80 ? 'is-haute' : v >= seuil ? 'is-moyenne' : 'is-basse'}`}>
          <b className="num">{v}</b>
        </div>
      ),
    },
    {
      title: 'Cible', dataIndex: 'nom', width: 92, align: 'center',
      render: (_, r) => (r.cible === 'pointure'
        ? <div className="dc-pointure">P{r.taille}</div>
        : <NdoeImage src={r.img} alt={r.nom} size="md" preview={false} />),
    },
    {
      title: 'Quoi', dataIndex: 'nom', width: 240,
      render: (nom, r) => (
        <div className="pp-produit dc-quoi">
          <b>{nom}</b>
          <small>{[r.marque, r.categorie].filter(Boolean).join(' · ') || (r.cible === 'pointure' ? 'Toutes marques' : '—')}</small>
        </div>
      ),
    },
    { title: 'Décision', dataIndex: 'type', width: 130, render: (_, r) => typeBadge(r) },
    {
      // La colonne UTILE : elle prend tout l'espace restant et respire.
      title: 'Pourquoi', dataIndex: 'raison',
      render: (v) => <span className="dc-raison">{v}</span>,
    },
    {
      title: 'Confiance', dataIndex: 'confiance', width: 110,
      render: (v, r) => (v
        ? <NdoeStatusBadge kind="custom" tone={r.confiance_tone} label={CONFIANCE_COURT[v] || r.confiance_label} />
        : <span className="dc-muted">—</span>),
    },
  ];

  const renderCard = (r) => (
    <NdoeDataCard
      key={r.id}
      media={r.cible === 'pointure'
        ? <div className="dc-pointure dc-pointure--big">P{r.taille}</div>
        : <NdoeImage src={r.img} alt={r.nom} size="lg" preview={false} />}
      title={r.nom}
      subtitle={r.raison}
      badge={typeBadge(r)}
      metrics={[
        { label: 'Priorité', value: `${r.priorite}/100` },
        ...r.preuve.slice(0, 2).map((p) => ({ label: p.label, value: p.valeur })),
      ]}
      onClick={() => setDetail(r)}
    />
  );

  const KpiCard = ({ label, value, gold, onClick }) => (
    onClick ? (
      <div className="pp-kpi-click" role="button" tabIndex={0} onClick={onClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}>
        <NdoeStatCard label={label} value={value} gold={gold} />
      </div>
    ) : <NdoeStatCard label={label} value={value} gold={gold} />
  );

  const t = summary.par_type || {};

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Rapports"
        title="Décisions recommandées"
        subtitle="Ce qu'il faut faire cette semaine — réassorts, pointures à renforcer, produits à relancer ou à solder."
        actions={
          <NdoeActionButton variant="secondary" icon={<DownloadOutlined />} loading={exporting} onClick={handleExport}>
            Exporter CSV
          </NdoeActionButton>
        }
      />

      <div className="pp-period">
        <NdoePeriodPicker value={period} onChange={setPeriod} />
        {meta.start_date && (
          <span className="pp-range">du {dateTxt(meta.start_date)} au {dateTxt(meta.end_date)}</span>
        )}
      </div>

      {/* Que faire cette semaine : les 5 actions les plus prioritaires. */}
      {(summary.top_semaine || []).length > 0 && (
        <section className="dc-semaine">
          <h3>Que faire cette semaine</h3>
          <ol>
            {summary.top_semaine.map((a) => (
              <li key={a.id}>
                <span className="dc-semaine__prio num">{a.priorite}</span>
                <div>
                  <b>{a.type_label} — {a.nom}</b>
                  <small>{a.raison}</small>
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      <NdoeStats className="pp-stats">
        <KpiCard label="Actions prioritaires" value={summary.actions_prioritaires ?? 0} gold />
        <KpiCard label="À réassortir" value={(t.reassort ?? 0) + (t.reassort_preventif ?? 0)}
          onClick={() => setFilter('type')('reassort')} />
        <KpiCard label="Pointures à renforcer" value={t.renforcer_pointure ?? 0}
          onClick={() => setFilter('type')('renforcer_pointure')} />
        <KpiCard label="À remettre en avant" value={t.remettre_en_avant ?? 0}
          onClick={() => setFilter('type')('remettre_en_avant')} />
        <KpiCard label="À solder" value={t.solder ?? 0}
          onClick={() => setFilter('type')('solder')} />
        <KpiCard label="À surveiller" value={summary.a_surveiller ?? 0}
          onClick={() => { setPrioritairesSeules(false); setFilter('type')('surveiller'); }} />
      </NdoeStats>

      <p className="pp-note rp-note">
        <NdoeStatusBadge kind="custom" tone="info" label="Comment lire" />
        {' '}Chaque décision vient d'un rapport source et transporte sa <b>raison</b> et ses <b>preuves chiffrées</b>&nbsp;;
        rien n'est réécrit ici. Par défaut, seules les <b>actions prioritaires</b> (priorité ≥ {seuil}) sont affichées&nbsp;:
        le reste ({(summary.actions_a_mener ?? 0) - (summary.actions_prioritaires ?? 0)} actions) est une longue traîne
        réelle mais non urgente — produits jamais vendus à tester, dormants à solder.
      </p>

      <ReportFilters
        q={q} setQ={setQ}
        filters={filters} setFilter={setFilter}
        refs={refs}
        sort="priorite" setSort={() => {}} sorts={SORTS}
        activeCount={activeCount}
      >
        <div className="ndoe-filter-bar__field">
          <Select placeholder="Décision" style={{ minWidth: 210 }} options={TYPES}
            value={filters.type} onChange={setFilter('type')} />
        </div>
        <div className="ndoe-filter-bar__field">
          <Select style={{ minWidth: 210 }}
            value={prioritairesSeules ? 'prio' : 'toutes'}
            onChange={(v) => setPrioritairesSeules(v === 'prio')}
            options={[
              { value: 'prio', label: `Actions prioritaires (≥ ${seuil})` },
              { value: 'toutes', label: 'Toutes les décisions' },
            ]} />
        </div>
      </ReportFilters>

      {error && !loading ? (
        <NdoeError text={error} onRetry={load} />
      ) : (
        <NdoeResponsiveTable
          className="pp-table"
          data={visibles}
          columns={columns}
          renderCard={renderCard}
          rowKey="id"
          loading={loading}
          error={error}
          onRetry={load}
          /* 656 px de colonnes fixes + Quoi/Pourquoi fluides : 1010 tient sur un
             portable 1366 sans pousser « Pourquoi » hors de l'écran (l'ancien
             1180 la tronquait, seule une barre de défilement la révélait). */
          scroll={{ x: 1010 }}
          onRow={(record) => ({ onClick: () => setDetail(record), style: { cursor: 'pointer' } })}
          pagination={{ pageSize: 25, showSizeChanger: false, hideOnSinglePage: true }}
          empty={{
            icon: '✅',
            title: 'Aucune décision prioritaire',
            text: 'Rien d’urgent sur ce périmètre. Passe à « Toutes les décisions » pour voir la longue traîne.',
          }}
        />
      )}

      <Drawer
        open={!!detail}
        onClose={() => setDetail(null)}
        width={520}
        title={detail?.nom || 'Détail de la décision'}
        className="pp-drawer"
      >
        {detail && (
          <div className="pp-detail">
            <div className="pp-detail__head">
              {detail.cible === 'pointure'
                ? <div className="dc-pointure dc-pointure--hero">P{detail.taille}</div>
                : <NdoeImage src={detail.img} alt={detail.nom} size="hero" />}
              <div className="pp-detail__meta">
                <b>{[detail.marque, detail.categorie].filter(Boolean).join(' · ') || 'Toutes marques'}</b>
                <small>Priorité {detail.priorite}/100</small>
                <div className="pp-detail__badges">
                  {typeBadge(detail)}
                  {detail.confiance && (
                    <NdoeStatusBadge kind="custom" tone={detail.confiance_tone}
                      label={CONFIANCE_COURT[detail.confiance] || detail.confiance_label} />
                  )}
                </div>
              </div>
            </div>

            <div className="rp-raison">
              <span className="rp-raison__label">Pourquoi cette décision</span>
              <p>{detail.raison}</p>
            </div>

            <h4 className="pp-detail__title">Les preuves</h4>
            <ul className="pp-detail__dates">
              {detail.preuve.map((p) => (
                <li key={p.label}><span>{p.label}</span><b>{p.valeur}</b></li>
              ))}
            </ul>

            <h4 className="pp-detail__title">Vérifier</h4>
            <p className="pp-detail__warn">
              Cette décision vient du rapport <b>{detail.source}</b>. Rien n'a été recalculé ici&nbsp;:
              la raison et les chiffres sont ceux du rapport d'origine.
            </p>
            <NdoeActionButton variant="secondary" block
              onClick={() => navigate(SOURCE_ROUTE[detail.source] || '/rapports/performance')}>
              Ouvrir le rapport {detail.source}
            </NdoeActionButton>
          </div>
        )}
      </Drawer>
    </NdoePage>
  );
}
